'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useRef, useState } from 'react'

const products = [
  {
    name: 'RowLab',
    href: 'https://rowlab.builtbysharan.com',
    subline: 'Edit, transform, and automate structured data',
    status: 'early-access',
    external: true,
  },
  {
    name: 'GhostRun CLI',
    href: 'https://ghostrun.builtbysharan.com',
    subline: 'The local-first QA agent for SaaS teams',
    status: 'live',
    external: true,
  },
  {
    name: 'Atlas',
    href: 'https://atlas.builtbysharan.com',
    subline: 'Stateful simulators for the APIs you depend on',
    status: 'beta',
    external: true,
  },
  {
    name: 'TeleportLink',
    href: 'https://teleport.builtbysharan.com',
    subline: 'Zero-knowledge, ephemeral file sharing',
    status: 'early-access',
    external: true,
  },
]

function ProductBadge({ status }: { status: string }) {
  if (status === 'live') {
    return (
      <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-xs font-mono whitespace-nowrap">
        Live
      </span>
    )
  }

  if (status === 'early-access') {
    return (
      <span className="px-2 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/30 text-amber-300 text-xs font-mono whitespace-nowrap">
        Early Access
      </span>
    )
  }

  if (status === 'beta') {
    return (
      <span className="px-2 py-0.5 rounded-full bg-violet-500/20 border border-violet-500/30 text-violet-300 text-xs font-mono whitespace-nowrap">
        Beta
      </span>
    )
  }

  return (
    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-mono whitespace-nowrap">
      In dev
    </span>
  )
}

export default function Navigation() {
  const [isProductsOpen, setIsProductsOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setIsProductsOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsProductsOpen(false)
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [])

  return (
    <nav className="global-navigation fixed top-0 left-0 right-0 z-[9999] isolate bg-bg-primary/80 backdrop-blur-sm border-b border-border-subtle w-full overflow-visible">
      <input id="mobile-menu-toggle" type="checkbox" className="peer hidden" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 w-full relative">
        <div className="flex items-center justify-between h-16 min-w-0">
          <Link href="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity duration-150 ease-out flex-shrink-0">
            <Image
              src="/logo.webp"
              alt="BBS Logo"
              width={32}
              height={32}
              className="object-contain sm:w-9 sm:h-9"
            />
            <span className="text-base sm:text-lg font-light text-text-primary whitespace-nowrap">
              BuiltBySharan
            </span>
          </Link>

          <div className="hidden md:flex items-center space-x-6 lg:space-x-8 flex-shrink-0 min-w-0">
            <span className="text-text-muted">|</span>

            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setIsProductsOpen((open) => !open)}
                className="text-sm text-text-primary hover:text-text-secondary transition-colors duration-150 ease-out font-light whitespace-nowrap flex items-center gap-1 select-none"
                aria-haspopup="menu"
                aria-expanded={isProductsOpen}
              >
                Products
                <svg
                  className={`w-3 h-3 transition-transform duration-150 ${isProductsOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {isProductsOpen ? (
                <div className="absolute left-1/2 top-full mt-3 w-72 -translate-x-1/2 bg-bg-secondary border border-border-subtle rounded-lg shadow-2xl ring-1 ring-black/20 z-[10001]">
                  <div className="py-2">
                    {products.map((product) => {
                      const Component = product.external ? 'a' : Link
                      const linkProps = product.external
                        ? { href: product.href, target: '_blank', rel: 'noopener noreferrer' }
                        : { href: product.href }

                      return (
                        <Component
                          key={product.name}
                          {...linkProps}
                          className="block px-4 py-3 hover:bg-bg-primary transition-colors duration-150 ease-out group"
                          onClick={() => setIsProductsOpen(false)}
                        >
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="font-medium text-text-primary group-hover:text-accent-primary transition-colors duration-150 ease-out flex items-center gap-1.5">
                              {product.name}
                              {product.external ? (
                                <svg className="w-3 h-3 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                              ) : null}
                            </div>
                            <ProductBadge status={product.status} />
                          </div>
                          <div className="text-xs text-text-secondary leading-relaxed">{product.subline}</div>
                        </Component>
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <Link href="/#labs" className="text-sm text-text-primary hover:text-text-secondary transition-colors duration-150 ease-out font-light whitespace-nowrap">Labs</Link>
            <Link href="/#open-source" className="text-sm text-text-primary hover:text-text-secondary transition-colors duration-150 ease-out font-light whitespace-nowrap">Open Source</Link>
            <Link href="/about" className="text-sm text-text-primary hover:text-text-secondary transition-colors duration-150 ease-out font-light whitespace-nowrap">About</Link>
            <Link href="/contact" className="text-sm text-text-primary hover:text-text-secondary transition-colors duration-150 ease-out font-light whitespace-nowrap">Contact</Link>
            <Link
              href="/contact"
              className="text-sm font-medium text-bg-primary bg-accent-primary hover:bg-accent-primary/90 transition-colors duration-150 ease-out whitespace-nowrap px-4 py-2 rounded-md"
            >
              Discuss an idea
            </Link>
          </div>

          <label
            htmlFor="mobile-menu-toggle"
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-md text-text-primary hover:bg-bg-secondary transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </label>
        </div>
      </div>

      <label
        htmlFor="mobile-menu-toggle"
        className="fixed inset-0 bg-black/60 opacity-0 pointer-events-none transition-opacity md:hidden peer-checked:opacity-100 peer-checked:pointer-events-auto"
        aria-hidden
      />
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-bg-primary border-l border-border-subtle shadow-xl translate-x-full transition-transform md:hidden peer-checked:translate-x-0">
        <div className="flex items-center justify-between h-16 px-4 border-b border-border-subtle flex-shrink-0">
          <span className="text-sm font-mono text-text-secondary">Menu</span>
          <label
            htmlFor="mobile-menu-toggle"
            className="flex items-center justify-center w-10 h-10 rounded-md text-text-primary hover:bg-bg-secondary transition-colors cursor-pointer"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </label>
        </div>
        <nav className="p-4 space-y-1 overflow-y-auto min-h-0">
          <p className="text-xs font-mono text-text-muted uppercase tracking-wider px-3 py-2">Products</p>
          {products.map((product) => {
            const Component = product.external ? 'a' : Link
            const linkProps = product.external
              ? { href: product.href, target: '_blank', rel: 'noopener noreferrer' }
              : { href: product.href }

            return (
              <Component
                key={product.name}
                {...linkProps}
                className="flex items-center justify-between gap-2 px-3 py-3 rounded-md hover:bg-bg-secondary transition-colors group"
              >
                <div className="min-w-0">
                  <span className="font-medium text-text-primary group-hover:text-accent-primary block truncate">{product.name}</span>
                  <span className="text-xs text-text-secondary block truncate">{product.subline}</span>
                </div>
                <ProductBadge status={product.status} />
              </Component>
            )
          })}
          <div className="border-t border-border-subtle my-4" />
          <Link href="/#labs" className="block px-3 py-3 rounded-md text-text-primary hover:bg-bg-secondary font-medium">Labs</Link>
          <Link href="/#open-source" className="block px-3 py-3 rounded-md text-text-primary hover:bg-bg-secondary font-medium">Open Source</Link>
          <Link href="/about" className="block px-3 py-3 rounded-md text-text-primary hover:bg-bg-secondary font-medium">About</Link>
          <Link href="/contact" className="block px-3 py-3 rounded-md text-text-primary hover:bg-bg-secondary font-medium">Contact</Link>
          <Link href="/contact" className="block px-3 py-3 rounded-md bg-accent-primary text-bg-primary font-medium text-center mt-2">Discuss an idea</Link>
        </nav>
      </div>
    </nav>
  )
}
